import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
  onConfirm: () => void;
  onCancel?: () => void;
  'data-testid'?: string;
  /** Optional extra content rendered between the description and the footer (e.g. an optional comment field). */
  children?: ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'default',
  onConfirm,
  onCancel,
  'data-testid': testId,
  children,
}: ConfirmDialogProps) {
  const { t } = useTranslation('core');
  const [isLoading, setIsLoading] = useState(false);
  // Derive: never show loading state when the dialog is closed
  const effectiveLoading = open && isLoading;

  const handleConfirm = async () => {
    setIsLoading(true);
    await Promise.resolve(onConfirm()).finally(() => setIsLoading(false));
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent role="alertdialog" className="sm:max-w-[425px]" data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={effectiveLoading}>
            {cancelLabel ?? t('confirmDialog.cancel')}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={effectiveLoading}
          >
            {effectiveLoading
              ? t('confirmDialog.processing')
              : (confirmLabel ?? t('confirmDialog.confirm'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
